import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Image,
  RefreshControl,
  ActivityIndicator,
  Animated,
  TextInput,
  ScrollView,
  Platform,
} from 'react-native';
import {BlurView} from '@react-native-community/blur';
import {useNavigation} from '@react-navigation/native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {Header} from '../components/Header';
import {Icon} from '../components/Icon';
import {useTheme} from '../hooks/useTheme';
import {Match} from '../types/diretta';
import {fetchMatches} from '../services/diretta';

type RootStackParamList = {
  Home: undefined;
  Diretta: {match: Match};
  Login: undefined;
};

type TabMode = 'calendario' | 'risultati';
type MatchResult = 'vittoria' | 'sconfitta' | 'pareggio';

// Helper to get Fiorentina's result
const getFiorentaResult = (match: Match): MatchResult | null => {
  if (match.status !== 'finished') return null;
  
  const homeIsFiorentina = match.home_team.name.toLowerCase().includes('fiorentina');
  const awayIsFiorentina = match.away_team.name.toLowerCase().includes('fiorentina');
  
  if (!homeIsFiorentina && !awayIsFiorentina) return null;
  
  const fiorentaScore = homeIsFiorentina ? match.score_home : match.score_away;
  const opponentScore = homeIsFiorentina ? match.score_away : match.score_home;
  
  if (fiorentaScore > opponentScore) return 'vittoria';
  if (fiorentaScore < opponentScore) return 'sconfitta';
  return 'pareggio';
};

export const CalendarioScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const {colors, isDark} = useTheme();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabMode>('calendario');
  const [notifiedMatches, setNotifiedMatches] = useState<Set<number>>(new Set());
  
  // New state for search and view mode
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  // Dynamic result badge colors based on theme
  const resultBadgeStyles = {
    vittoria: {
      bg: isDark ? '#1a4d2e' : '#d4edda', 
      text: isDark ? '#6bcf8a' : '#155724', 
      label: 'V'
    },
    sconfitta: {
      bg: isDark ? '#4d1a1a' : '#f8d7da', 
      text: isDark ? '#f08080' : '#721c24', 
      label: 'P'
    },
    pareggio: {
      bg: isDark ? '#4d4419' : '#fff3cd', 
      text: isDark ? '#f0d060' : '#856404', 
      label: 'X'
    },
  };

  const loadMatches = async () => {
    try {
      const data = await fetchMatches();
      setMatches(data);
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, []);

  // Auto-refresh every 60s to detect live matches
  useEffect(() => {
    const interval = setInterval(() => {
      loadMatches();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Detect live match
  const liveMatch = useMemo(() => {
    return matches.find(m => m.status === 'live') || null;
  }, [matches]);

  // Pulsing animation for live dot
  const pulseAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ]),
    );
    pulse.start();
    return () => pulse.stop();
  }, [pulseAnim]);

  // Handle month selection when switching tabs or when data loads
  useEffect(() => {
    const now = new Date();
    if (activeTab === 'risultati') {
      // Check if current month has any results
      const currentMonthHasResults = matches.some(m => {
        const mDate = new Date(m.date);
        return mDate < now && 
               mDate.getMonth() === now.getMonth() && 
               mDate.getFullYear() === now.getFullYear();
      });
      
      if (!currentMonthHasResults) {
        // Fallback to last month
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        setSelectedMonth(lastMonth);
      } else {
        setSelectedMonth(now);
      }
    } else {
      // Calendario: start at current month
      setSelectedMonth(now);
    }
  }, [activeTab, matches]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadMatches();
    setRefreshing(false);
  };

  const filteredMatches = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Search filter
    const searchLower = searchQuery.toLowerCase().trim();
    const matchesSearch = (match: Match) => {
      if (!searchLower) return true;
      return (
        match.home_team.name.toLowerCase().includes(searchLower) ||
        match.away_team.name.toLowerCase().includes(searchLower)
      );
    };

    if (activeTab === 'calendario') {
      return matches
        .filter((match) => match.status !== 'live' && new Date(match.date) >= today && matchesSearch(match))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    } else {
      return matches
        .filter((match) => new Date(match.date) < today && matchesSearch(match))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
  }, [matches, activeTab, searchQuery]);

  const handleMatchPress = (match: Match) => {
    navigation.navigate('Diretta', {match});
  };

  const formatMatchDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const formatted = date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    });
    // Capitalize weekday and month: "sabato 14 feb" -> "Sabato 14 Feb"
    return formatted
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getMatchTime = (match: Match) => {
    // Use time from API if available, otherwise extract from date
    if (match.time) return match.time;
    const date = new Date(match.date);
    return date.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'});
  };

  // Render team logo or placeholder
  const renderTeamLogo = (teamName: string, logoUrl: string | null | undefined) => {
    if (logoUrl) {
      return (
        <Image 
          source={{uri: logoUrl}} 
          style={styles.teamLogo}
        />
      );
    }
    // Placeholder with initial
    const initial = teamName.charAt(0).toUpperCase();
    return (
      <View style={[styles.teamLogoPlaceholder, {backgroundColor: colors.primaryPastel}]}>
        <Text style={[styles.teamLogoPlaceholderText, {color: colors.primary}]}>{initial}</Text>
      </View>
    );
  };

  // Animated Bell Component
  const AnimatedBell = ({matchId}: {matchId: number}) => {
    const shakeAnim = useRef(new Animated.Value(0)).current;
    const [isLoading, setIsLoading] = useState(false);
    const isActive = notifiedMatches.has(matchId);

    const handleBellPress = useCallback(() => {
      setIsLoading(true);
      
      // Shake animation
      Animated.sequence([
        Animated.timing(shakeAnim, {toValue: 10, duration: 50, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: -10, duration: 50, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: 10, duration: 50, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: -10, duration: 50, useNativeDriver: true}),
        Animated.timing(shakeAnim, {toValue: 0, duration: 50, useNativeDriver: true}),
      ]).start(() => {
        // After animation, toggle the state
        setIsLoading(false);
        setNotifiedMatches(prev => {
          const newSet = new Set(prev);
          if (isActive) {
            newSet.delete(matchId); // Deactivate
          } else {
            newSet.add(matchId); // Activate
          }
          return newSet;
        });
      });
    }, [isActive, matchId, shakeAnim]);

    const bellColor = isActive ? '#4CAF50' : colors.primary;
    const bgColor = isActive ? (isDark ? '#1a4d2e' : '#d4edda') : colors.primaryPastel;

    return (
      <TouchableOpacity onPress={handleBellPress} activeOpacity={0.7}>
        <Animated.View 
          style={[
            styles.notifyBadge, 
            {backgroundColor: bgColor, transform: [{rotate: shakeAnim.interpolate({inputRange: [-10, 10], outputRange: ['-10deg', '10deg']})}]}
          ]}
        >
          {isLoading ? (
            <ActivityIndicator size="small" color={bellColor} />
          ) : (
            <Icon name="bell" size={16} color={bellColor} />
          )}
        </Animated.View>
      </TouchableOpacity>
    );
  };

  const renderMatch = ({item}: {item: Match}) => {
    const result = getFiorentaResult(item);
    const isFinished = item.status === 'finished';
    const isLive = item.status === 'live';
    const isScheduled = !isFinished && !isLive;

    // Handle press: only allow for finished/live matches
    const handlePress = () => {
      if (!isScheduled) {
        handleMatchPress(item);
      }
      // For scheduled matches, do nothing (notification bell is separate)
    };

    return (
      <TouchableOpacity
        style={[styles.matchCard, {backgroundColor: colors.cardBackground, shadowColor: colors.textPrimary}]}
        onPress={handlePress}
        activeOpacity={isScheduled ? 1 : 0.7}
        disabled={isScheduled}
      >
        {/* Date at top */}
        <Text style={[styles.dateTop, {color: colors.textSecondary}]}>{formatMatchDate(item.date)}</Text>
        
        <View style={styles.matchRow}>
          {/* Competition Icon */}
          {item.competition && (
            <Image 
              source={{uri: item.competition}} 
              style={styles.listCompetitionIcon} 
              resizeMode="contain"
            />
          )}
          
          {/* Team info with logos */}
          <View style={styles.teamsColumn}>
            {/* Home team */}
            <View style={styles.teamRow}>
              {renderTeamLogo(item.home_team.name, item.home_team.logo)}
              <Text style={[styles.teamName, {color: colors.textPrimary}]} numberOfLines={1}>
                {item.home_team.name}
              </Text>
            </View>

            {/* Away team */}
            <View style={styles.teamRow}>
              {renderTeamLogo(item.away_team.name, item.away_team.logo)}
              <Text style={[styles.teamName, {color: colors.textPrimary}]} numberOfLines={1}>
                {item.away_team.name}
              </Text>
            </View>
          </View>

          {/* Score or Time column */}
          <View style={styles.scoreColumn}>
            {isScheduled ? (
              // Show time for scheduled matches
              <Text style={[styles.matchTime, {color: colors.primary}]}>{getMatchTime(item)}</Text>
            ) : (
              // Show score for finished/live matches
              <>
                <Text style={[styles.score, {color: colors.textSecondary}, isFinished && {color: colors.textPrimary}]}>
                  {item.score_home}
                </Text>
                <Text style={[styles.scoreSeparator, {color: colors.textSecondary}]}>-</Text>
                <Text style={[styles.score, {color: colors.textSecondary}, isFinished && {color: colors.textPrimary}]}>
                  {item.score_away}
                </Text>
              </>
            )}
          </View>

          {/* Result badge for finished matches */}
          {isFinished && result && (
            <View style={[styles.resultBadge, {backgroundColor: resultBadgeStyles[result].bg}]}>
              <Text style={[styles.resultBadgeText, {color: resultBadgeStyles[result].text}]}>
                {resultBadgeStyles[result].label}
              </Text>
            </View>
          )}

          {/* Live badge */}
          {isLive && (
            <View style={styles.liveBadge}>
              <View style={styles.liveDot} />
            </View>
          )}

          {/* Arrow for playable matches, Bell for scheduled matches */}
          {isScheduled ? (
            <AnimatedBell matchId={item.id ?? 0} />
          ) : (
            <Icon name="chevron-right" size={20} color={colors.textSecondary} />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, {backgroundColor: colors.background}]}>
        <Header />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  // Live Match Banner
  const renderLiveBanner = () => {
    if (!liveMatch) return null;
    return (
      <TouchableOpacity
        style={[styles.liveBannerContainer, {
          borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(68,18,116,0.25)',
          shadowColor: isDark ? '#000' : 'rgba(68,18,116,0.35)',
        }]}
        activeOpacity={0.85}
        onPress={() => navigation.navigate('Diretta', {match: liveMatch})}
      >
        {/* Liquid glass background */}
        {Platform.OS === 'ios' && (
          <BlurView
            style={StyleSheet.absoluteFill}
            blurType={isDark ? 'ultraThinMaterialDark' : 'ultraThinMaterial'}
            blurAmount={40}
            reducedTransparencyFallbackColor={isDark ? 'rgba(30,30,30,0.85)' : 'rgba(245,245,245,0.85)'}
          />
        )}
        {Platform.OS === 'android' && (
          <View style={[StyleSheet.absoluteFill, {
            backgroundColor: isDark ? 'rgba(30,30,35,0.85)' : 'rgba(255,255,255,0.85)',
          }]} />
        )}
        {/* Purple tint overlay */}
        <View style={[StyleSheet.absoluteFill, {
          backgroundColor: isDark ? 'rgba(68,18,116,0.35)' : 'rgba(68,18,116,0.08)',
        }]} />
        {/* Inner glow border */}
        <View style={[styles.liveBannerInnerGlow, {
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.6)',
        }]} />

        {/* Scoreboard content */}
        <View style={styles.liveBannerContent}>
          {/* Home team: logo + name below */}
          <View style={styles.liveBannerTeamCol}>
            {liveMatch.home_team.logo ? (
              <Image source={{uri: liveMatch.home_team.logo}} style={styles.liveBannerLogo} />
            ) : (
              <View style={[styles.liveBannerLogoPlaceholder, {backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(68,18,116,0.1)'}]}>
                <Text style={[styles.liveBannerLogoText, {color: isDark ? '#fff' : '#441274'}]}>{liveMatch.home_team.name.charAt(0)}</Text>
              </View>
            )}
            <Text style={[styles.liveBannerTeamName, {color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'}]} numberOfLines={1}>{liveMatch.home_team.name}</Text>
          </View>

          {/* Center: score + DIRETTA badge */}
          <View style={styles.liveBannerCenter}>
            <Text style={[styles.liveBannerScoreText, {color: isDark ? '#fff' : '#1a1a1a'}]}>
              {liveMatch.score_home} - {liveMatch.score_away}
            </Text>
            <View style={styles.liveBannerBadge}>
              <Animated.View style={[styles.liveBannerBadgeDot, {opacity: pulseAnim}]} />
              <Text style={styles.liveBannerBadgeText}>DIRETTA</Text>
              <Icon name="chevron-right" size={11} color="#fff" />
            </View>
          </View>

          {/* Away team: logo + name below */}
          <View style={styles.liveBannerTeamCol}>
            {liveMatch.away_team.logo ? (
              <Image source={{uri: liveMatch.away_team.logo}} style={styles.liveBannerLogo} />
            ) : (
              <View style={[styles.liveBannerLogoPlaceholder, {backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(68,18,116,0.1)'}]}>
                <Text style={[styles.liveBannerLogoText, {color: isDark ? '#fff' : '#441274'}]}>{liveMatch.away_team.name.charAt(0)}</Text>
              </View>
            )}
            <Text style={[styles.liveBannerTeamName, {color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)'}]} numberOfLines={1}>{liveMatch.away_team.name}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={[styles.container, {backgroundColor: colors.background}]}>
      <Header />

      {/* Live match banner — above everything */}
      {renderLiveBanner()}
      
      {/* Tab Selector */}
      <View style={[styles.tabContainer, {backgroundColor: colors.background}]}>
        <TouchableOpacity
          style={[styles.tab, {backgroundColor: colors.surfaceVariant}, activeTab === 'calendario' && {backgroundColor: colors.primary}]}
          onPress={() => setActiveTab('calendario')}
        >
          <View style={styles.tabContent}>
            <Icon name="calendar-days-outline" size={14} color={activeTab === 'calendario' ? '#fff' : colors.textSecondary} />
            <Text style={[styles.tabText, {color: colors.textSecondary}, activeTab === 'calendario' && styles.activeTabText]}>
              Calendario
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, {backgroundColor: colors.surfaceVariant}, activeTab === 'risultati' && {backgroundColor: colors.primary}]}
          onPress={() => setActiveTab('risultati')}
        >
          <View style={styles.tabContent}>
            <Icon name="clock-rotate-left" size={14} color={activeTab === 'risultati' ? '#fff' : colors.textSecondary} />
            <Text style={[styles.tabText, {color: colors.textSecondary}, activeTab === 'risultati' && styles.activeTabText]}>
              Risultati
            </Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Search Bar and View Toggle */}
      <View style={styles.searchRow}>
        <View style={[styles.searchContainer, {backgroundColor: colors.surfaceVariant}]}>
          <Icon name="magnifying-glass" size={16} color={colors.textSecondary} />
          <TextInput
            style={[styles.searchInput, {color: colors.textPrimary}]}
            placeholder="Cerca squadra..."
            placeholderTextColor={colors.textSecondary}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Icon name="xmark" size={16} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.viewToggleRow}>
          <TouchableOpacity 
            style={[styles.viewToggleBtn, viewMode === 'list' && {backgroundColor: colors.primary}]}
            onPress={() => setViewMode('list')}
          >
            <Icon name="chart-simple" size={16} color={viewMode === 'list' ? '#fff' : colors.textSecondary} />
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.viewToggleBtn, viewMode === 'calendar' && {backgroundColor: colors.primary}]}
            onPress={() => setViewMode('calendar')}
          >
            <Icon name="calendar-days" size={16} color={viewMode === 'calendar' ? '#fff' : colors.textSecondary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Conditional rendering based on view mode */}
      {viewMode === 'list' ? (
      <FlatList
        data={filteredMatches}
        renderItem={renderMatch}
        keyExtractor={(item) => item.match_id}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <View style={styles.emptyIconContainer}>
              <Icon 
                name={activeTab === 'calendario' ? 'calendar-days' : 'chart-simple'} 
                size={48} 
                color={colors.textSecondary} 
              />
            </View>
            <Text style={[styles.emptyTitle, {color: colors.textPrimary}]}>
              {activeTab === 'calendario' ? 'Nessuna partita in programma' : 'Nessun risultato'}
            </Text>
            <Text style={[styles.emptyText, {color: colors.textSecondary}]}>
              {activeTab === 'calendario' 
                ? 'Le prossime partite della Fiorentina appariranno qui.'
                : 'I risultati delle partite giocate appariranno qui.'}
            </Text>
          </View>
        }
        showsVerticalScrollIndicator={false}
      />
      ) : (
        /* Calendar View */
        <ScrollView 
          style={styles.calendarContainer}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          {/* Month Navigation */}
          {(() => {
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const selectedMonthNum = selectedMonth.getMonth();
            const selectedYear = selectedMonth.getFullYear();
            
            // Calendario: can't go to past months (disable left arrow)
            // Risultati: can't go to future months (disable right arrow)
            const canGoBack = activeTab === 'risultati' || 
              (selectedYear > currentYear || (selectedYear === currentYear && selectedMonthNum > currentMonth));
            const canGoForward = activeTab === 'calendario' || 
              (selectedYear < currentYear || (selectedYear === currentYear && selectedMonthNum < currentMonth));

            return (
              <View style={styles.monthNav}>
                <TouchableOpacity 
                  onPress={() => canGoBack && setSelectedMonth(prev => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() - 1);
                    return d;
                  })}
                  disabled={!canGoBack}
                  style={{opacity: canGoBack ? 1 : 0.3}}
                >
                  <Icon name="chevron-left" size={20} color={colors.textPrimary} />
                </TouchableOpacity>
                <Text style={[styles.monthTitle, {color: colors.textPrimary}]}>
                  {selectedMonth.toLocaleDateString('it-IT', {month: 'long', year: 'numeric'}).replace(/^\w/, c => c.toUpperCase())}
                </Text>
                <TouchableOpacity 
                  onPress={() => canGoForward && setSelectedMonth(prev => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() + 1);
                    return d;
                  })}
                  disabled={!canGoForward}
                  style={{opacity: canGoForward ? 1 : 0.3}}
                >
                  <Icon name="chevron-right" size={20} color={colors.textPrimary} />
                </TouchableOpacity>
              </View>
            );
          })()}

          {/* Matches for selected month */}
          {(() => {
            const monthMatches = filteredMatches.filter(m => {
              const mDate = new Date(m.date);
              return mDate.getMonth() === selectedMonth.getMonth() && 
                     mDate.getFullYear() === selectedMonth.getFullYear();
            });

            if (monthMatches.length === 0) {
              return (
                <View style={styles.emptyMonthContainer}>
                  <Text style={[styles.emptyMonthText, {color: colors.textSecondary}]}>
                    Nessuna partita in questo mese
                  </Text>
                </View>
              );
            }

            return monthMatches.map((match, idx) => {
              const matchDate = new Date(match.date);
              const isFiorentaHome = match.home_team.name.toLowerCase().includes('fiorentina');
              const rival = isFiorentaHome ? match.away_team : match.home_team;
              const result = getFiorentaResult(match);
              const isFinished = match.status === 'finished';

              return (
                <TouchableOpacity 
                  key={match.match_id || idx}
                  style={[styles.calendarRow, {backgroundColor: colors.cardBackground, borderColor: colors.border}]}
                  onPress={() => isFinished && handleMatchPress(match)}
                  disabled={!isFinished}
                >
                  <View style={styles.calendarDateCol}>
                    <Text style={[styles.calendarDay, {color: colors.textPrimary}]}>
                      {matchDate.getDate()}
                    </Text>
                    <Text style={[styles.calendarWeekday, {color: colors.textSecondary}]}>
                      {matchDate.toLocaleDateString('it-IT', {weekday: 'short'}).slice(0, 3).toUpperCase()}
                    </Text>
                  </View>
                  
                  {/* Rival Logo with Competition Icon */}
                  <View style={styles.rivalLogoContainer}>
                    {rival.logo ? (
                      <Image source={{uri: rival.logo}} style={styles.calendarRivalLogo} />
                    ) : (
                      <View style={[styles.calendarRivalPlaceholder, {backgroundColor: colors.primaryPastel}]}>
                        <Text style={[styles.calendarRivalInitial, {color: colors.primary}]}>
                          {rival.name.charAt(0)}
                        </Text>
                      </View>
                    )}
                    {/* Competition Icon - bottom left of logo */}
                    {match.competition && (
                      <Image 
                        source={{uri: match.competition}} 
                        style={styles.competitionIcon} 
                        resizeMode="contain"
                      />
                    )}
                  </View>

                  <Text style={[styles.calendarRivalName, {color: colors.textPrimary}]} numberOfLines={1}>
                    {rival.name}
                  </Text>
                  
                  {/* Home/Away Icon - after team name */}
                  <View style={[styles.homeAwayBadge, {backgroundColor: isFiorentaHome ? '#22c55e' : '#3b82f6'}]}>
                    <Icon name={isFiorentaHome ? 'house-solid' : 'globe-solid'} size={10} color="#fff" />
                  </View>

                  {/* Score or Time */}
                  {isFinished ? (
                    <Text style={[styles.calendarScore, {color: colors.textPrimary}]}>
                      {match.score_home} - {match.score_away}
                    </Text>
                  ) : (
                    <Text style={[styles.calendarTime, {color: colors.primary}]}>
                      {match.time || new Date(match.date).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}
                    </Text>
                  )}

                  {/* Result indicator */}
                  {isFinished && result && (
                    <View style={[styles.calendarResultBadge, {backgroundColor: resultBadgeStyles[result].bg}]}>
                      <Text style={[styles.calendarResultText, {color: resultBadgeStyles[result].text}]}>
                        {resultBadgeStyles[result].label}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            });
          })()}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 20,
    marginHorizontal: 4,
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  activeTabText: {
    color: '#fff',
  },
  list: {
    padding: 16,
    paddingBottom: 100,
  },
  matchCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 6,
    elevation: 2,
  },
  matchRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  teamsColumn: {
    flex: 1,
  },
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
  },
  teamLogo: {
    width: 22,
    height: 22,
    marginRight: 8,
    borderRadius: 2,
  },
  teamLogoPlaceholder: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#E8DEFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  teamLogoPlaceholderText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#441274',
  },
  teamName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  listCompetitionIcon: {
    width: 24,
    height: 24,
    marginRight: 10,
    borderRadius: 4,
  },
  scoreColumn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 50,
    marginHorizontal: 8,
  },
  score: {
    fontSize: 16,
    fontWeight: '700',
    color: '#999',
    width: 18,
    textAlign: 'center',
  },
  scoreFinished: {
    color: '#333',
  },
  scoreSeparator: {
    fontSize: 14,
    color: '#999',
    marginHorizontal: 2,
  },
  matchTime: {
    fontSize: 15,
    fontWeight: '700',
    color: '#441274',
  },
  resultBadge: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
  },
  resultBadgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  liveBadge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#4CAF50',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#fff',
  },
  arrow: {
    fontSize: 18,
    color: '#ccc',
    marginLeft: 6,
  },
  notifyBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    marginLeft: 6,
  },
  dateTop: {
    fontSize: 12,
    fontWeight: '500' as const,
    marginBottom: 10,
  },
  date: {
    fontSize: 11,
    color: '#999',
    marginTop: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    marginTop: 40,
  },
  emptyIconContainer: {
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
  },
  // Search and view toggle styles
  searchRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 12,
    alignItems: 'center',
    gap: 8,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    padding: 0,
  },
  viewToggleRow: {
    flexDirection: 'row',
    gap: 4,
  },
  viewToggleBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Calendar view styles
  calendarContainer: {
    flex: 1,
    paddingHorizontal: 16,
  },
  monthNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  emptyMonthContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyMonthText: {
    fontSize: 14,
  },
  calendarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
    marginBottom: 8,
    borderWidth: 1,
  },
  rivalLogoContainer: {
    position: 'relative',
  },
  competitionIcon: {
    position: 'absolute',
    bottom: -4,
    left: -4,
    width: 16,
    height: 16,
    borderRadius: 3,
  },
  calendarDateCol: {
    width: 36,
    alignItems: 'center',
    marginRight: 12,
  },
  calendarDay: {
    fontSize: 18,
    fontWeight: '700',
  },
  calendarWeekday: {
    fontSize: 10,
    fontWeight: '500',
  },
  calendarRivalLogo: {
    width: 32,
    height: 32,
    borderRadius: 4,
  },
  calendarRivalPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarRivalInitial: {
    fontSize: 14,
    fontWeight: '700',
  },
  homeAwayBadge: {
    width: 20,
    height: 20,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6,
  },
  homeAwayText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
  },
  calendarRivalName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 10,
  },
  calendarScore: {
    fontSize: 14,
    fontWeight: '700',
    marginHorizontal: 8,
  },
  calendarTime: {
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 8,
  },
  calendarResultBadge: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarResultText: {
    fontSize: 11,
    fontWeight: '700',
  },
  // Live Match Banner
  liveBannerContainer: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    shadowOffset: {width: 0, height: 6},
    shadowOpacity: 0.25,
    shadowRadius: 14,
    elevation: 8,
  },
  liveBannerInnerGlow: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 15,
    borderWidth: 1,
  },
  liveBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  liveBannerTeamCol: {
    alignItems: 'center',
    width: 80,
    gap: 6,
  },
  liveBannerLogo: {
    width: 36,
    height: 36,
    borderRadius: 4,
  },
  liveBannerLogoPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  liveBannerLogoText: {
    fontSize: 14,
    fontWeight: '700',
  },
  liveBannerTeamName: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  liveBannerCenter: {
    alignItems: 'center',
    gap: 6,
  },
  liveBannerScoreText: {
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: 2,
  },
  liveBannerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#E57373',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  liveBannerBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#fff',
  },
  liveBannerBadgeText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
    color: '#fff',
  },
});
